import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";
import { managementActions } from "../action-creators";
import type { DynamicsExportState } from "../type";

const DYNAMICS_EXPORT_SETTINGS_STATE: DynamicsExportState = {
    settings: {
        accounts: [],
        contacts: [],
        leads: [],
        companyAsLeads: [],
    },
    columns: {
        accounts: [],
        contacts: [],
        leads: [],
        companyAsLeads: [],
    },
    isColumnsLoading: false,
    isLoadingSettings: false,
    prevSettings: {
        accounts: [],
        contacts: [],
        leads: [],
        companyAsLeads: [],
    },
    settingDeleteError: undefined,
    settingsCreateError: undefined,
    settingsFetchError: undefined,
    isSavingSettings: false,
    isSavedSettings: false,
    saveSettingsError: undefined,
    editableField: undefined,
    deletableField: undefined,
    exportingDomainsTo: false,
    exportingDomainsToError: undefined,
    showModalDisabledExport: false,
    notification: undefined,
    exportCompaniesStatus: false,
    loadingExportCompaniesStatus: false,
    settingExportCompaniesStatus: false,
    exportCompaniesStatusError: undefined,
};

export const dynamicsExportReducer = createReducer<DynamicsExportState, ActionType<typeof managementActions.dynamics>>(
    DYNAMICS_EXPORT_SETTINGS_STATE,
)
    .handleAction(managementActions.dynamics.setDynamicsDeletableField, (state, { payload }) => ({
        ...state,
        deletableField: payload,
    }))
    .handleAction(managementActions.dynamics.setDynamicsEditableField, (state, { payload }) => ({
        ...state,
        editableField: payload,
    }))
    .handleAction(
        managementActions.dynamics.fetchExportSettingsTableAsync.success,
        (state, { payload }) => ({
            ...state,
            prevSettings: {
                ...state.prevSettings,
                [payload.typeName]: payload.settings,
            },
            settings: {
                ...state.settings,
                [payload.typeName]: payload.settings,
            },
            isLoadingSettings: false,
        }),
    )
    .handleAction(managementActions.dynamics.fetchExportSettingsTableAsync.request, (state) => ({
        ...state,
        isLoadingSettings: true,
    }))
    .handleAction(
        managementActions.dynamics.fetchExportSettingsTableAsync.failure,
        (state, { payload }) => ({
            ...state,
            isLoadingSettings: false,
            settingsFetchError: payload,
        }),
    )
    .handleAction(managementActions.dynamics.fetchDynamicsExportSettingsAsync.success, (state, { payload }) => ({
        ...state,
        prevSettings: payload,
        settings: payload,
        isLoadingSettings: false,
    }))
    .handleAction(managementActions.dynamics.fetchDynamicsExportSettingsAsync.request, (state) => ({
        ...state,
        isLoadingSettings: true,
    }))
    .handleAction(managementActions.dynamics.fetchDynamicsExportSettingsAsync.failure, (state, { payload }) => ({
        ...state,
        isLoadingSettings: false,
        settingsFetchError: payload,
    }))
    .handleAction(managementActions.dynamics.saveExportSettingsAsync.success, (state, { payload }) => ({
        ...state,
        prevSettings: {
            ...state.prevSettings,
            [payload.typeName]: payload.settings,
        },
        settings: {
            ...state.settings,
            [payload.typeName]: payload.settings,
        },
        isSavingSettings: false,
        isSavedSettings: true,
    }))
    .handleAction(managementActions.dynamics.saveExportSettingsAsync.request, (state) => ({
        ...state,
        isSavingSettings: true,
    }))
    .handleAction(managementActions.dynamics.saveExportSettingsAsync.failure, (state, { payload }) => ({
        ...state,
        isSavingSettings: false,
        saveSettingsError: payload,
    }))
    .handleAction(managementActions.dynamics.exportDomainsToAsync.success, (state) => ({
        ...state,
        exportingDomainsTo: false,
    }))
    .handleAction(managementActions.dynamics.exportDomainsToAsync.request, (state) => ({
        ...state,
        exportingDomainsTo: true,
    }))
    .handleAction(managementActions.dynamics.exportDomainsToAsync.failure, (state, { payload }) => ({
        ...state,
        exportingDomainsTo: false,
        exportingDomainsToError: payload,
    }))
    .handleAction(managementActions.dynamics.showModalDisabledExport, (state, { payload }) => ({
        ...state,
        showModalDisabledExport: payload,
    }))
    .handleAction(managementActions.dynamics.setDynamicsNotification, (state, { payload }) => ({
        ...state,
        notification: payload,
    }))
    .handleAction(managementActions.dynamics.addNewSettingsExport, (state, { payload: { typeName, settings } }) => ({
        ...state,
        settings: {
            ...state.settings,
            [typeName]: [...state.settings[typeName], ...settings],
        },
    }))
    .handleAction(managementActions.dynamics.updateDynamicsSettingsExport, (state, { payload }) => ({
        ...state,
        settings: {
            ...state.settings,
            [payload.typeName]: payload.settings,
        },
    }))
    .handleAction(managementActions.dynamics.deleteSettingExport, (state, { payload }) => ({
        ...state,
        settings: {
            ...state.settings,
            [payload.typeName]: payload.settings,
        },
        deletableField: undefined,
    }))
    .handleAction(managementActions.dynamics.revertSettingsExport, (state, { payload }) => ({
        ...state,
        settings: {
            ...state.settings,
            [payload.typeName]: payload.settings,
        },
    }))
    .handleAction(managementActions.dynamics.getDynamicsColumnsAsync.request, (state) => ({
        ...state,
        isColumnsLoading: true,
    }))
    .handleAction(managementActions.dynamics.getDynamicsColumnsAsync.success, (state, { payload }) => ({
        ...state,
        columns: payload,
        isColumnsLoading: false,
    }))
    .handleAction(managementActions.dynamics.getDynamicsColumnsAsync.failure, (state) => ({
        ...state,
        isColumnsLoading: false,
    }))
    .handleAction(managementActions.dynamics.saveDynamicsAllSettingsError, (state, { payload }) => ({
        ...state,
        saveSettingsError: payload,
    }));
